<template lang="pug">
    BlockUI.overflow-hidden(ref="blockUi")
        BackgroundComponent(
            v-show="loadingMessage !== ''"
        )
        BlockingMessageComponent(
            :message="loadingMessage"
            v-show="loadingMessage !== ''"
        )
        Toast(
            :id="toastId"
            :class="compIsActive ? 'visible' : 'invisible'"
            :group="toastId"
            :pt:root:style="{ position: 'absolute' }"
        )
        .h-dvh.flex.flex-col
            .flex.items-baseline
                MainMenu(
                :id="mainMenuId"
                    :haveFile="haveFile"
                    :editorStatus="editorStatus"
                    :noPython="noPython"
                    :viewState="viewState"
                    @about="onAboutMenu"
                    @edit-action="onEditAction"
                    @export-action="onExportAction"
                    @file-action="onFileAction"
                    @menu-active="onMenuActive"
                    @view-action="onViewAction"
                )
                div.flex-grow.text-center.font-bold {{ windowTitle }}
                div.text-xs.pr-2(
                    v-if="!updateAvailable"
                ) v{{ version.currentVersion }}
                button.update-link.flex.mr-1.text-xs.cursor-pointer.rounded-sm.border.border-transparent.bg-transparent(
                    v-if="updateAvailable"
                    class="px-1.5 py-0.5"
                    type="button"
                    title="Click to reload and update"
                    @click="onUpdateClick"
                ) New version available!
                ToggleButton.editor-dark-selector(
                    v-model="darkMode"
                    offIcon="pi pi-sun"
                    onIcon="pi pi-moon"
                    size="small"
                    @change="onDarkMode"
                )
            ConfirmDialog
                template(#message="slotProps")
                    i(:class="slotProps.message.icon" style="font-size: 2rem;")
                    span {{ slotProps.message.message }}
            CellDLEditor.grow(
                :editorCommand="editorCommand"
                :theme="editorTheme"
                @editorData="onEditorData"
                @error="onError"
            )
            AboutDialog(
                v-model:visible="aboutVisible"
                @close="aboutVisible = false"
            )
            Dialog.issues(
                v-model:visible="cellmlIssuesVisible"
                modal=""
            )
                template(#header)
                    .flex.w-full
                        p.text-2xl.font-bold Issues {{ cellmlIssuesKind }} CellML:
                        .grow
                        Button(
                            icon="pi pi-copy"
                            title="Copy to clipboard"
                            @click="copyIssuesToClipboard"
                        )
                div
                    p.mb-1(
                        v-for="issue in cellmlIssues"
                    ) {{ issue }}
</template>

<style>
.editor-dark-selector .p-togglebutton-label {
    display: none;
}
</style>

<style scoped>
.update-link {
    color: var(--p-primary-color);
}

.update-link:hover {
    background-color: var(--p-content-hover-background);
    border-color: var(--p-content-border-color);
}
</style>

<script setup lang="ts">
import * as vue from 'vue'
import * as vueusecore from '@vueuse/core'

import 'primeicons/primeicons.css'

import primeVueAuraTheme from '@primeuix/themes/aura'
import primeVueConfig from 'primevue/config'
import ConfirmationService from 'primevue/confirmationservice';
import primeVueToastService from 'primevue/toastservice';
import { useConfirm } from "primevue/useconfirm"
import { useToast } from 'primevue/usetoast';

import JSZip from 'jszip'

//==============================================================================

import '@celldl/editor/style.css'

import type {
    CellDLEditorCommand,
    EditorData,

    Theme,
    ViewState
} from '@celldl/editor'

import { DEFAULT_VIEW_STATE, EditorStatus, type FileStatus } from '@celldl/editor'

import { bgRdfStatements } from '@celldl/editor'

import * as $rdf from '@celldl/rdf'

import {
    type CellMLOutput,
    celldl2cellml,
    initialisePython,
    type PyodideAPI,
    type RdfInterface
} from '@celldl/editor-python-tools'
import { loadInBrowser } from '@celldl/editor-python-tools/web'

//==============================================================================

import '../assets/app.css'

import { SHORT_DELAY } from '../common/constants'
import { isCompatibleBrowser } from '../common/common'
import * as version from '../common/version'
import * as vueCommon from '../common/vueCommon'

import AboutDialog from './dialogs/AboutDialog.vue'

//==============================================================================

type IEditorAppProps = {
    theme?: Theme
    noPython?: boolean
}

const props = defineProps<IEditorAppProps>()

//==============================================================================

const loadingMessage = vue.ref<string>('Loading CellDL editor')
let pythonInitialised = false

// We need to load the RDF module before using the CellDL Editor component

const CellDLEditor = vue.defineAsyncComponent(async () => {
    await $rdf.initialise()

    if (!props.noPython) {
        vue.nextTick(async () => {      // To see loading messages ??
            const rdfI: RdfInterface = {
                getRdfStatements: bgRdfStatements,
                oximockRdfModule: $rdf.oxiRdfModule
            }
            await loadInBrowser().then(async (pyApi: PyodideAPI) => {
                console.log(pyApi)
                await initialisePython(pyApi, rdfI, async (msg: string) => {
                    console.log(msg)
                    loadingMessage.value = msg
                })
            })
            loadingMessage.value = ''
            console.log('Editor ready...')
            pythonInitialised = true
        })
    } else {
        loadingMessage.value = ''
        console.log('Editor ready...')
    }

    return import('@celldl/editor')
})

// We are now fully loaded, so start checking for a newer version of the editor

version.startCheck()

//==============================================================================
//==============================================================================

// Setup PrimeVue's theme and confirmation service

const crtInstance = vue.getCurrentInstance();

if (crtInstance) {
    const app = crtInstance.appContext.app;

    if (!app.config.globalProperties.$primevue) {
        app.use(primeVueConfig as unknown as vue.Plugin, {
            theme: {
                preset: primeVueAuraTheme,
                options: {
                    darkModeSelector: '.celldl-dark-mode'
                }
            }
        })
    }

    if (!app.config.globalProperties.$confirm) {
        app.use(ConfirmationService as unknown as vue.Plugin)
    }

    if (!app.config.globalProperties.$toast) {
        app.use(primeVueToastService as unknown as vue.Plugin)
    }
}

vueCommon.useTheme().setTheme(props.theme)

const confirm = useConfirm()

const toast = useToast()

const blockUi = vue.ref<vue.ComponentPublicInstance | null>(null)
const toastId = vue.ref('editorToast')
const mainMenuId = vue.ref('editorMainMenu')
const activeInstanceUid = vueCommon.activeInstanceUid()

//==============================================================================

const editorTheme = vue.ref<Theme|undefined>(props.theme)

const darkMode = vue.ref<boolean>(vueCommon.useTheme().useDarkMode())

function onDarkMode() {
    if (darkMode.value) {
        editorTheme.value = 'dark'
    } else {
        editorTheme.value = 'light'
    }
    vueCommon.useTheme().setTheme(editorTheme.value)
}

//==============================================================================

// Keep track of which instance of the CellDL Editor is currently active.

function activateInstance(): void {
    activeInstanceUid.value = String(crtInstance?.uid)
}

const compIsActive = vue.computed(() => {
    return activeInstanceUid.value === String(crtInstance?.uid)
})

vue.onMounted(() => {
    const blockUiElement = blockUi.value?.$el as HTMLElement

    // Let the user know their browser is not supported

    vue.nextTick().then(() => {
        if (!isCompatibleBrowser()) {
             confirm.require({
                header: 'Incompatible browser...',
                message: `Either Google Chrome or Microsoft Edge is needed to open and save CellDL diagrams.`,
                icon: 'pi pi-exclamation-triangle',
                rejectClass: 'invisible',
                acceptProps: {
                    label: 'OK'
                }
            })
        }
    })

    // Customise our IDs.

    toastId.value = `editorToast${String(crtInstance?.uid)}`
    mainMenuId.value = `opencorMainMenu${String(crtInstance?.uid)}`

    // Make ourselves the active instance.

    setTimeout(() => {
        activateInstance()
    }, SHORT_DELAY)

    // Ensure that our toasts are shown within our block UI.

    setTimeout(() => {
        const toastElement = document.getElementById(toastId.value)

        if (toastElement) {
            blockUiElement.appendChild(toastElement)
        }
    }, SHORT_DELAY)
})

//==============================================================================
//==============================================================================

const editorCommand = vue.ref<CellDLEditorCommand>()

//==============================================================================

const windowTitle = vue.ref<string>('New diagram')

const fileStatus = vue.ref<FileStatus>({
    haveData: false,
    modified: false
})

const beforeUnloadHandler = (event: Event) => {
    event.preventDefault()
}

function diagramModified(modified: boolean) {
    fileStatus.value.modified = modified
    if (modified) {
        window.addEventListener("beforeunload", beforeUnloadHandler)
    } else {
        window.removeEventListener("beforeunload", beforeUnloadHandler)
    }
}

const haveFile = vue.computed(() => {
    return fileStatus.value.haveData
})

const fileModified = vue.computed(() => {
    return fileStatus.value.modified
})

let currentFileHandle: FileSystemFileHandle|undefined

//==============================================================================
//==============================================================================

vueusecore.useEventListener(document, 'file-edited', (_: Event) => {
    fileStatus.value.haveData = true
    diagramModified(true)
    if (!windowTitle.value.endsWith(' *')) {
        windowTitle.value += ' *'
    }
})

//==============================================================================

async function onEditorData(data: EditorData) {
    if (data.kind?.startsWith('export')) {
        if (pythonInitialised) {
            const parsed = data.kind.split('-')
            const exportKind = (parsed.length > 1) ? parsed[1] : 'cellml'
            await exportAsCellML(data.data, exportKind as string)
        }
    } else if (data.kind === 'save-as' || !currentFileHandle) {
        await saveFile(data.data)
    } else if (currentFileHandle) {
        await writeFileData(currentFileHandle, data.data)
    }
}

//==============================================================================

function onError(msg: string) {
    window.alert(msg)
}

//==============================================================================
//==============================================================================

async function onFileAction(action: string) {
    if (action === 'new') {
        await onNewFile()
    } else if (action === 'open') {
        await onOpenFile()
    } else if (action === 'save') {
        await onSaveFile(false)
    } else if (action === 'save-as') {
        await onSaveFile(true)
    }
}

//==============================================================================

async function onNewFile() {
    if (!fileStatus.value.modified) {
        closeFile()
    } else {
        confirm.require({
            message: 'Close modified diagram?',
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            rejectProps: {
                label: 'Cancel',
                severity: 'secondary',
                outlined: true
            },
            acceptProps: {
                label: 'Close'
            },
            accept: () => {
                closeFile()
            }
        })
    }
}

function closeFile() {
    editorCommand.value = {
        command: 'file',
        options: {
            action: 'close'
        }
    }
    currentFileHandle = undefined
    fileStatus.value.haveData = false
    diagramModified(false)
    windowTitle.value = 'New diagram'
}

//==============================================================================

async function onOpenFile() {
    if (!fileStatus.value.modified) {
        await openFile()
    } else {
        confirm.require({
            message: 'Overwrite modified diagram?',
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            rejectProps: {
                label: 'Cancel',
                severity: 'secondary',
                outlined: true
            },
            acceptProps: {
                label: 'Open'
            },
            accept: async () => {
                await openFile()
            }
        })
    }
}

async function openFile() {
    const options = {
        excludeAcceptAllOption: true,
        types: [
            {
                description: 'CellDL files',
                accept: {
                    'image/svg+xml': ['.celldl', '.svg'],
                }
            }
        ]
    }
    const fileHandles = await window.showOpenFilePicker(options)
    if (fileHandles.length) {
        currentFileHandle = fileHandles[0]
        if (currentFileHandle) {
            const file = await currentFileHandle.getFile()
            const contents = await file.text()
            editorCommand.value = {
                command: 'file',
                options: {
                    action: 'open',
                    data: contents,
                    name: currentFileHandle.name
                }
            }
            fileStatus.value.haveData = true
            diagramModified(false)
            windowTitle.value = currentFileHandle.name
        }
    }
}

//==============================================================================

async function onSaveFile(saveAs: boolean=false) {
    editorCommand.value = {
        command: 'file',
        options: {
            action: 'data',
            kind: saveAs || !currentFileHandle ? 'save-as' : 'save'
        }
    }
}

async function saveFile(celldl: string) {
    const fileName = currentFileHandle?.name || undefined
    const options = {
        types: [
            {
                description: 'CellDL files',
                accept: {
                    'image/svg+xml': ['.svg', '.celldl'],
                }
            }
        ],
        suggestedName: fileName
    }
    const fileHandle = await window.showSaveFilePicker(options).catch(() => {})
    if (fileHandle) {
        await writeFileData(fileHandle, celldl)
        currentFileHandle = fileHandle
    }
}

async function writeFileData(fileHandle: FileSystemFileHandle, data: string) {
    const writableStream = await fileHandle.createWritable()
    await writableStream.write(data)
    await writableStream.close()
    diagramModified(false)
    windowTitle.value = fileHandle.name
    editorCommand.value = {
        command: 'edit',
        options: {
            action: 'clean'
        }
    }
}

//==============================================================================
//==============================================================================

const MODEL_URI_PREFIX = 'https://celldl.org/cellml/'

function OMEX_SPEC_URL(term: string): string {
    return `http://identifiers.org/combine.specifications/${term}`
}

function withSuffix(filename: string, suffix: string): string {
    const parts = filename.split('.')
    if (parts.length === 1) {
        parts.push(suffix)
    } else {
        parts[parts.length-1] = suffix
    }
    return parts.join('.')
}

const cellmlIssues = vue.ref<string[]>([])
const cellmlIssuesKind = vue.ref('generating')
const cellmlIssuesVisible = vue.ref(false)

function copyIssuesToClipboard() {
    navigator.clipboard.writeText(cellmlIssues.value.join('\n'))
}

//==============================================================================

async function onExportAction(action: string) {
    if (action === 'cellml' || action === 'omex') {
        editorCommand.value = {
            command: 'file',
            options: {
                action: 'data',
                kind: `export-${action}`
            }
        }
    }
}

async function exportAsCellML(celldl: string, exportKind: string) {
    let fileName = `Untitled.${exportKind}`
    if (currentFileHandle) {
        fileName = withSuffix(currentFileHandle.name, exportKind)
    }
    const options = {
        types: [
            {
                description: 'CellML files',
                accept: {
                    'application/cellml+xml': ['.cellml'],  // .omex  omex mediatype + zip
                    'application/zip': ['.omex'],  // .omex  omex mediatype + zip
                }
            }
        ],
        suggestedName: fileName
    }
    const fileHandle = await window.showSaveFilePicker(options).catch(() => {})
    if (fileHandle) {
        const celldl_file_name = currentFileHandle ? currentFileHandle.name
                                                   : withSuffix(fileHandle.name, 'svg')
        const cellml_file_name = withSuffix(exportKind === 'omex'
                                          ? celldl_file_name
                                          : fileHandle.name, 'cellml')
        const exportedObject = await celldl2cellml(celldl, {
            sourceUri: `${MODEL_URI_PREFIX}${celldl_file_name}`,
            cellmlUri: `${MODEL_URI_PREFIX}${cellml_file_name}`,
            metadata: (exportKind === 'omex')
        })

        if (exportedObject.issues) {
            cellmlIssues.value = exportedObject.issues
            cellmlIssuesVisible.value = true
        } else if (exportedObject.cellml) {
            if (exportKind === 'omex') {
                const omex = new JSZip()
                const content: Record<string, string>[] = []
                content.push({
                    location: '.',
                    format: OMEX_SPEC_URL('omex')
                })
                omex.file(celldl_file_name, celldl)
                content.push({
                    location: celldl_file_name,
                    format: 'image/svg+xml'
                })

                const cellml = exportedObject.cellml.replaceAll(MODEL_URI_PREFIX, '')
                omex.file(cellml_file_name, cellml)
                content.push({
                    location: cellml_file_name,
                    format: OMEX_SPEC_URL('cellml'),
                    master: 'true'
                })
                let annotation = exportedObject.metadata
                if (annotation) {
                    annotation = annotation.replaceAll(MODEL_URI_PREFIX, '')
                    const annotation_file = withSuffix(celldl_file_name, 'ttl')
                    omex.file(annotation_file, annotation)
                    content.push({
                        location: annotation_file,
                        format: OMEX_SPEC_URL('omex-metadata'),
                    })
                }
                const manifest = `<?xml version='1.0' encoding='UTF-8' standalone='yes'?>
<omexManifest xmlns="http://identifiers.org/combine.specifications/omex-manifest">
  ${content.map(c => {
    const attributes = Object.entries(c).map(kv => `${kv[0]}="${kv[1]}"`)
    return `<content ${attributes.join(' ')}/>`
  }).join('\n  ')}
</omexManifest>`
                omex.file('manifest.xml', manifest)
                omex.generateAsync({type:"blob"})
                .then(async (blob) => {
                    const stream = await fileHandle.createWritable()
                    await stream.write(blob)
                    await stream.close()
                })
            } else {
                const writableStream = await fileHandle.createWritable()
                await writableStream.write(exportedObject.cellml)
                await writableStream.close()
            }
            toast.add({
                severity: 'info',
                group: toastId.value,
                summary: 'CellML created',
                detail: `Saved as ${fileHandle.name}`,
                life: 3000
            })
        } else {
            window.alert(`Unexpected exception generating CellML: ${exportedObject.exception}`)
        }
    }
}

//==============================================================================
//==============================================================================

function onMenuActive() {
    editorCommand.value = {
        command: 'set-state',
        options: {
            action: 'reset-tools'
        }
    }
}

//==============================================================================

const editorStatus = vue.computed<EditorStatus>(() => new EditorStatus())

function onEditAction(action: string) {
    editorCommand.value = {
        command: 'edit',
        options: {
            action: action
        }
    }
}

//==============================================================================

const viewState = vue.ref<ViewState>({ ...DEFAULT_VIEW_STATE })

function onViewAction(action: string, value: number|boolean) {
    if (action === 'show-grid') {
        viewState.value = { ...viewState.value, showGrid: !!value }
        editorCommand.value = {
            command: 'view',
            options: viewState.value
        }
    } else if (action === 'show-guides') {
        viewState.value = { ...viewState.value, alignmentGuides: !!value }
        editorCommand.value = {
            command: 'view',
            options: viewState.value
        }
    } else if (action === 'snap-to-grid') {
        viewState.value = { ...viewState.value, snapToGrid: Number(value) }
        editorCommand.value = {
            command: 'view',
            options: viewState.value
        }
    }
}

//==============================================================================
//==============================================================================

// About dialog.

const aboutVisible = vue.ref<boolean>(false)

function onAboutMenu(): void {
  aboutVisible.value = true
}


//==============================================================================
//==============================================================================

// Handle the click on the update link button by showing a Yes/No dialog.

const updateConfirmVisible = vue.ref(false)
const updateAvailable = version.updateAvailable
const latestVersion = version.latestVersion

const onUpdateClick = () => {
    confirm.require({
        header: 'Update Available...',
        message: `Version '${latestVersion.value}' is available. Do you want to reload and update now?`,
        icon: 'pi pi-info-circle',
        rejectProps: {
            label: 'No',
            severity: 'secondary',
            outlined: true
        },
        acceptProps: {
            label: 'Yes'
        },
        accept: () => {
            version.reloadApp()
        }
    })
}

//==============================================================================
//==============================================================================

</script>
